import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Truck, Route as RouteIcon, IndianRupee, Clock, AlertTriangle, Receipt, Plus, Fuel, FileCheck, Phone, Timer, PackageX, PackageSearch } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend, LineChart, Line } from "recharts";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, StatusBadge, Field, statusLabel } from "@/components/common";
import { ExpenseForm } from "@/components/trip";

const LEVEL_STYLE = {
  green: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400",
  amber: "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400",
  red: "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-400",
};
const LEVEL_LABEL = { green: "Normal loading", amber: "Halt warning (2 days)", red: "High detention / penalty" };
const dwell = (h) => (h >= 24 ? `${Math.floor(h / 24)}d ${Math.round(h % 24)}h` : `${Math.round(h)}h`);

function QuickActions({ onChanged }) {
  const navigate = useNavigate();
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [podOpen, setPodOpen] = useState(false);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [expenseBooking, setExpenseBooking] = useState("");
  const [vehId, setVehId] = useState("");
  const [vehStatus, setVehStatus] = useState("Available");
  const openExpense = () => { api.get("/bookings").then((r) => { setBookings(r.data.filter((b) => b.status !== "Delivered")); setExpenseBooking(""); setExpenseOpen(true); }); };
  const openPod = () => { api.get("/bookings").then((r) => { setBookings(r.data.filter((b) => b.status !== "Created")); setPodOpen(true); }); };
  const openVehicle = () => { api.get("/vehicles").then((r) => { setVehicles(r.data); setVehId(""); setVehicleOpen(true); }); };
  const saveVehicle = async () => {
    const v = vehicles.find((x) => x.id === vehId);
    if (!v) return toast.error("Select a vehicle");
    try { await api.put(`/vehicles/${vehId}`, { ...v, status: vehStatus }); toast.success(`${v.vehicle_number} → ${vehStatus}`); setVehicleOpen(false); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const btn = "flex items-center gap-2 rounded-lg border bg-card px-3.5 py-2 text-sm font-semibold shadow-sm transition-all hover:border-primary hover:text-primary hover:shadow";
  return (
    <>
      <div className="mb-6 flex flex-wrap gap-2 fade-up" data-testid="quick-actions">
        <button data-testid="qa-new-booking" className={btn} onClick={() => navigate("/bookings/new")}><Plus className="h-4 w-4" />New Booking / LR</button>
        <button data-testid="qa-record-expense" className={btn} onClick={openExpense}><Fuel className="h-4 w-4" />Record Expense</button>
        <button data-testid="qa-update-pod" className={btn} onClick={openPod}><FileCheck className="h-4 w-4" />Update POD</button>
        <button data-testid="qa-vehicle-status" className={btn} onClick={openVehicle}><Truck className="h-4 w-4" />Update Vehicle Status</button>
      </div>
      <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
        <DialogContent className="max-w-md bg-card" data-testid="qa-expense-dialog">
          <DialogHeader><DialogTitle>Record Trip Expense</DialogTitle></DialogHeader>
          <Field label="Trip (LR)">
            <select data-testid="qa-expense-booking-select" className="native-select" value={expenseBooking} onChange={(e) => setExpenseBooking(e.target.value)}>
              <option value="">Select trip…</option>
              {bookings.map((b) => <option key={b.id} value={b.id}>{b.lr_number} · {b.pickup_location} → {b.drop_location} · {statusLabel(b.status)}</option>)}
            </select>
          </Field>
          {expenseBooking && <ExpenseForm bookingId={expenseBooking} onAdded={() => { setExpenseOpen(false); onChanged(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={podOpen} onOpenChange={setPodOpen}>
        <DialogContent className="max-w-md bg-card" data-testid="qa-pod-dialog">
          <DialogHeader><DialogTitle>Update POD — pick a trip</DialogTitle></DialogHeader>
          <div className="max-h-72 divide-y overflow-y-auto">
            {bookings.map((b) => (
              <button key={b.id} data-testid={`qa-pod-${b.lr_number}`} onClick={() => { setPodOpen(false); navigate(`/bookings/${b.id}`); }}
                className="flex w-full items-center justify-between gap-2 px-1 py-2.5 text-left text-sm hover:bg-secondary/60">
                <div><span className="font-mono font-semibold text-primary">{b.lr_number}</span><span className="ml-2 text-muted-foreground">{b.pickup_location} → {b.drop_location}</span></div>
                {b.pod_files?.length ? <span className="text-xs text-emerald-600">{b.pod_files.length} POD</span> : <span className="text-xs font-semibold text-rose-600">Missing POD</span>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={vehicleOpen} onOpenChange={setVehicleOpen}>
        <DialogContent className="max-w-md bg-card" data-testid="qa-vehicle-dialog">
          <DialogHeader><DialogTitle>Update Vehicle Status</DialogTitle></DialogHeader>
          <Field label="Vehicle">
            <select data-testid="qa-vehicle-select" className="native-select" value={vehId} onChange={(e) => { const v = vehicles.find((x) => x.id === e.target.value); setVehId(e.target.value); setVehStatus(v?.status || "Available"); }}>
              <option value="">Select vehicle…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number} · {v.status}</option>)}
            </select>
          </Field>
          <Field label="New Status">
            <select data-testid="qa-vehicle-status-select" className="native-select" value={vehStatus} onChange={(e) => setVehStatus(e.target.value)}>
              {["Available", "On Trip", "Maintenance"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Button data-testid="qa-vehicle-save-btn" onClick={saveVehicle}>Update Status</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function WidgetCard({ icon: Icon, label, value, hint, tone = "text-primary", onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="card-base fade-up p-5 text-left transition-colors hover:border-primary/50">
      <div className="flex items-start justify-between"><span className="label-xs">{label}</span><span className={`rounded-lg bg-accent p-2 ${tone}`}><Icon className="h-4 w-4" /></span></div>
      <div className="mt-3 text-2xl font-semibold amount tracking-tight">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
      <div className="mt-2 text-xs font-semibold text-primary">View details →</div>
    </button>
  );
}

function DetentionList({ items }) {
  return (
    <div className="space-y-3" data-testid="detention-list">
      {items.map((x) => (
        <div key={x.id} className="flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm">
          <div className="min-w-0 flex-1">
            <Link to={`/bookings/${x.id}`} className="font-mono font-semibold text-primary hover:underline">{x.lr_number}</Link>
            <span className="ml-2 font-mono text-xs">{x.vehicle_number}</span>
            <div className="text-xs text-muted-foreground">{x.driver_name} · {x.driver_phone} · {x.pickup_location}</div>
            <div className="text-xs text-muted-foreground">Parked since {fmtDate(x.parked_at)} · dwell {dwell(x.dwell_hours)}</div>
          </div>
          <span data-testid={`detention-badge-${x.lr_number}`} className={`rounded-full border px-2.5 py-1 text-xs font-bold ${LEVEL_STYLE[x.level]}`}>
            {x.level === "green" ? "🟢" : x.level === "amber" ? "🟠" : "🔴"} {LEVEL_LABEL[x.level]}
          </span>
        </div>
      ))}
      {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No vehicles parked at loading points.</p>}
    </div>
  );
}

function OverdueList({ items }) {
  return (
    <div className="space-y-3" data-testid="overdue-list">
      {items.map((x) => (
        <div key={x.id} className="rounded-lg border border-rose-200 bg-rose-50/50 p-3 text-sm dark:border-rose-900 dark:bg-rose-950/20">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><Link to={`/bookings/${x.id}`} className="font-mono font-semibold text-primary hover:underline">{x.lr_number}</Link>
              <span className="ml-2 font-mono text-xs">{x.vehicle_number}</span>
              <span className="ml-2 rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`overdue-delay-${x.lr_number}`}>+{x.delay_hours}h late</span></div>
            <div className="flex gap-2">
              {x.driver_phone && <a href={`tel:${x.driver_phone}`} data-testid={`overdue-call-${x.lr_number}`} className="flex items-center gap-1 rounded-md border bg-card px-2.5 py-1 text-xs font-semibold hover:border-primary"><Phone className="h-3 w-3" /> Call {x.driver_name}</a>}
              <Link to={`/bookings/${x.id}`} data-testid={`overdue-update-${x.lr_number}`} className="flex items-center gap-1 rounded-md border bg-card px-2.5 py-1 text-xs font-semibold hover:border-primary">Update status</Link>
            </div>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{x.pickup_location} → {x.drop_location} · ETA was {fmtDate(x.eta)} · Driver {x.driver_name}</div>
        </div>
      ))}
      {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No overdue vehicles. All trips are within ETA.</p>}
    </div>
  );
}

function PodList({ items, notFit }) {
  return (
    <div className="space-y-2" data-testid={notFit ? "pod-notfit-list" : "pod-missing-list"}>
      {items.map((x) => (
        <Link key={x.id} to={`/bookings/${x.id}`} data-testid={`pod-item-${x.lr_number}`} className="flex items-center justify-between rounded-lg border p-3 text-sm hover:border-primary">
          <div><span className="font-mono font-semibold text-primary">{x.lr_number}</span><div className="text-xs text-muted-foreground">{x.customer_name} · delivered {fmtDate(x.delivered_at)}</div></div>
          {notFit ? <span className="amount text-xs font-bold text-rose-600">{inr(x.claim_amount)}</span> : <span className="text-xs font-semibold text-rose-600">Upload POD</span>}
        </Link>
      ))}
      {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{notFit ? "No damaged/short deliveries." : "All delivered trips have POD."}</p>}
    </div>
  );
}

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];
const tip = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 } };

function Panel({ title, children, testid, className = "" }) {
  return (
    <div data-testid={testid} className={`card-base p-5 md:p-6 fade-up ${className}`}>
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      {children}
    </div>
  );
}

function IdleAlert() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/reports/idle-days").then((r) => setRows(r.data.rows.filter((x) => x.current_idle != null && x.current_idle > 3))).catch(() => {});
  }, []);
  if (!rows.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200 fade-up" data-testid="idle-alert">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />{rows.length} gaadi 3 din se zyada se khadi hai</div>
        <Link to="/idle-days" className="text-xs font-semibold underline" data-testid="idle-alert-link">Idle Days Report dekhein</Link>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {rows.slice(0, 12).map((r) => (
          <span key={r.vehicle_id} className="rounded-md bg-white/70 px-2 py-1 text-xs font-semibold dark:bg-black/20" data-testid={`idle-alert-${r.vehicle_id}`}>
            {r.vehicle_number} · {r.current_idle} din{r.last_lr ? ` (since ${r.last_lr})` : ""}
          </span>
        ))}
        {rows.length > 12 && <span className="px-2 py-1 text-xs">+{rows.length - 12} more</span>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { role, companyName } = useAuth();
  const [d, setD] = useState(null);
  const [modal, setModal] = useState(null);
  const load = () => api.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;
  const isAdmin = role === "admin";
  const monthLabel = (m) => new Date(m + "-01").toLocaleDateString("en-IN", { month: "short" });

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`${companyName} · operations overview`} />
      <QuickActions onChanged={load} />
      {role !== "driver" && <IdleAlert />}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
        <StatCard testid="kpi-active-fleet" label="Active Fleet" value={`${d.active_fleet}/${d.total_fleet}`} hint={`${d.fleet_status["On Trip"]} on trip · ${d.fleet_status.Maintenance} in maintenance`} icon={Truck} />
        <StatCard testid="kpi-ongoing-trips" label="Ongoing Trips" value={d.ongoing_trips} hint={`${d.trip_status.Loading} loading · ${d.trip_status["In-Transit"]} delivery`} icon={RouteIcon} accent="text-amber-600" delay={60} />
        {isAdmin ? (
          <>
            <StatCard testid="kpi-monthly-revenue" label="Monthly Revenue" value={inr(d.monthly_revenue)} hint={`Expenses ${inr(d.monthly_expenses)}`} icon={IndianRupee} accent="text-emerald-600" delay={120} />
            <StatCard testid="kpi-pending-payments" label="Pending Payments" value={inr(d.pending_payments)} hint="Outstanding from customers" icon={Clock} accent="text-rose-600" delay={180} />
          </>
        ) : (
          <>
            <StatCard testid="kpi-created" label="Awaiting Dispatch" value={d.trip_status.Created} hint="Bookings not yet loaded" icon={Clock} delay={120} />
            <StatCard testid="kpi-pending-bills" label="Bills Pending Approval" value={d.pending_bills} icon={Receipt} delay={180} />
          </>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6" data-testid="tracker-widgets">
        <WidgetCard testid="widget-detention" icon={Timer} label="At Loading Point" value={d.detention.length}
          hint={d.detention.some((x) => x.level === "red") ? `${d.detention.filter((x) => x.level === "red").length} in high detention` : "Vehicles parked for loading"} tone="text-amber-600" onClick={() => setModal("detention")} />
        <WidgetCard testid="widget-overdue" icon={AlertTriangle} label="Overdue Transit" value={d.overdue.length}
          hint={d.overdue.length ? `Longest delay ${Math.max(...d.overdue.map((x) => x.delay_hours))}h` : "All within ETA"} tone="text-rose-600" onClick={() => setModal("overdue")} />
        <WidgetCard testid="widget-pod-missing" icon={PackageSearch} label="POD Missing" value={d.pod_missing_count}
          hint="Loading Point trips without POD" tone="text-amber-600" onClick={() => setModal("pod_missing")} />
        <WidgetCard testid="widget-pod-notfit" icon={PackageX} label="POD Not Fit" value={d.pod_not_fit_count}
          hint={`Damage/shortage claims ${inr(d.pod_claim_total)}`} tone="text-rose-600" onClick={() => setModal("pod_not_fit")} />
      </div>

      {d.overdue.length > 0 && (
        <div className="card-base mt-6 p-5 fade-up" data-testid="overdue-section">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold"><AlertTriangle className="h-5 w-5 text-rose-600" /> Overdue Running Vehicles</h3>
          <OverdueList items={d.overdue} />
        </div>
      )}

      <Dialog open={!!modal} onOpenChange={() => setModal(null)}>
        <DialogContent className="max-w-2xl bg-card max-h-[85vh] overflow-y-auto" data-testid="tracker-modal">
          <DialogHeader><DialogTitle>
            {modal === "detention" && "Loading Point Detention"}
            {modal === "overdue" && "Overdue Running Vehicles"}
            {modal === "pod_missing" && "Loading Point Trips Missing POD"}
            {modal === "pod_not_fit" && "Damaged / Short Deliveries (POD Not Fit)"}
          </DialogTitle></DialogHeader>
          {modal === "detention" && <DetentionList items={d.detention} />}
          {modal === "overdue" && <OverdueList items={d.overdue} />}
          {modal === "pod_missing" && <PodList items={d.pod_missing} />}
          {modal === "pod_not_fit" && <PodList items={d.pod_not_fit} notFit />}
        </DialogContent>
      </Dialog>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {isAdmin && (
          <Panel title="Revenue vs Expenses" testid="chart-revenue-trend" className="lg:col-span-2">
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={d.revenue_trend.map((x) => ({ ...x, month: monthLabel(x.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v / 1000}k`} />
                  <Tooltip {...tip} formatter={(v) => inr(v)} cursor={{ fill: "hsl(var(--secondary))" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        )}
        {isAdmin && (
          <Panel title="Expense Breakdown" testid="chart-expense-breakdown">
            <div className="h-48">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={d.expense_breakdown} dataKey="amount" nameKey="type" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {d.expense_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % 5]} />)}
                  </Pie>
                  <Tooltip {...tip} formatter={(v) => inr(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1.5">
              {d.expense_breakdown.map((e, i) => (
                <div key={e.type} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 capitalize"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS[i % 5] }} />{e.type}</span>
                  <span className="amount">{inr(e.amount)}</span>
                </div>
              ))}
            </div>
          </Panel>
        )}
        <Panel title="Trip Count Trends" testid="chart-trips-trend" className={isAdmin ? "lg:col-span-3" : "lg:col-span-3"}>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={d.trips_weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip {...tip} cursor={{ stroke: "hsl(var(--border))" }} />
                <Line type="monotone" dataKey="trips" name="Bookings (weekly)" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap gap-4">
            {d.trips_monthly.map((m) => (
              <div key={m.month} className="rounded-lg bg-secondary/60 px-3 py-1.5 text-xs" data-testid={`trips-month-${m.month}`}>
                <span className="text-muted-foreground">{monthLabel(m.month)}:</span> <b className="amount">{m.trips}</b> delivered
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Recent Bookings" testid="recent-bookings" className={isAdmin ? "lg:col-span-2" : "lg:col-span-2"}>
          <div className="divide-y">
            {d.recent_bookings.map((b) => (
              <Link key={b.id} to={`/bookings/${b.id}`} data-testid={`recent-booking-${b.lr_number}`} className="flex items-center justify-between gap-3 py-3 transition-colors hover:text-primary">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-muted-foreground">{b.lr_number} · {fmtDate(b.booking_date)}</div>
                  <div className="truncate text-sm font-medium">{b.pickup_location} → {b.drop_location}</div>
                  <div className="truncate text-xs text-muted-foreground">{b.customer_name}{b.vehicle_number ? ` · ${b.vehicle_number}` : ""}</div>
                </div>
                <StatusBadge status={b.status} />
              </Link>
            ))}
          </div>
        </Panel>
        <Panel title="Alerts" testid="doc-alerts">
          {d.doc_alerts.length === 0 && d.inventory_issues.length === 0 && <p className="text-sm text-muted-foreground">All documents valid and no inventory issues.</p>}
          <div className="space-y-2">
            {d.doc_alerts.map((a, i) => (
              <div key={i} className={`flex items-center gap-3 rounded-lg border p-3 text-sm ${a.expired ? "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30" : "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30"}`}>
                <AlertTriangle className={`h-4 w-4 shrink-0 ${a.expired ? "text-rose-600" : "text-amber-600"}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs font-semibold">{a.vehicle_number}</div>
                  <div className="text-xs text-muted-foreground">{a.doc} {a.expired ? "expired" : "expires"} {fmtDate(a.date)}</div>
                </div>
              </div>
            ))}
            {d.inventory_issues.map((h) => (
              <div key={h.id} data-testid={`inv-alert-${h.id}`} className="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm dark:border-rose-900 dark:bg-rose-950/30">
                <PackageX className="h-4 w-4 shrink-0 text-rose-600" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs font-semibold">{h.vehicle_number} · {h.driver_name}</div>
                  <div className="text-xs text-muted-foreground">{h.type === "handover" ? "Handover" : "Return"} check {fmtDate(h.checked_at)}: <span className="text-rose-700 dark:text-rose-300">{h.issues.join(", ")}</span></div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
